import React, { useState } from 'react';
import { Trip, TripMember, ItineraryItem } from '../../types/trip';
import { Expense, Settlement } from '../../types/expense';
import { useDesign } from '../../designs';
import { addExpense, updateExpense, deleteExpense, calculateSettlements } from '../../services/expenses';
import { saveSettlement } from '../../services/trips';
import Modal from '../../components/ui/Modal';
import OrnamentalDivider from '../../components/ui/OrnamentalDivider';
import DesignButton from '../../components/ui/DesignButton';
import { getTripDays, formatDayHeader, fmtDollars } from '../../utils/dates';
import { getDisplayMembers } from '../../utils/members';

interface ExpensesTabProps { trip: Trip; tripId: string; canEdit: boolean; currentUid: string; }
type SubTab = 'expenses' | 'settle';

/* ─── Logged Expense Row ─── */
function ExpenseRow({ expense, members, onEdit }: {
  expense: Expense; members: Record<string, TripMember>; onEdit: () => void;
}) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const displayFont = `'${design.typography.displayFont}', serif`;
  const payerName = members[expense.paidByUid]?.displayName?.split(' ')[0] || 'Unknown';
  const splitCount = (expense.splitAmong || []).length;
  const perEach = splitCount > 0 ? Number(expense.amount) / splitCount : Number(expense.amount);
  const isNoSplit = expense.noSplit;
  const splitLabel = isNoSplit ? '' : splitCount > 1 ? `Split ${splitCount} ways` : `Split ${splitCount}`;

  return (
    <div
      onClick={onEdit}
      style={{
        borderLeft: `2px solid ${isNoSplit ? 'rgba(147,142,164,0.20)' : 'rgba(200,146,58,0.22)'}`,
        borderRadius: '0 2px 2px 0',
        backgroundColor: 'rgba(27,20,39,0.60)',
        padding: '8px 14px', marginBottom: '6px',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        {/* Left column: name + payer (tight pair) */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyLarge, fontWeight: 500, color: d.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, lineHeight: 1.25 }}>
              {expense.description}
            </span>
            {isNoSplit && (
              <span style={{
                fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, letterSpacing: '1px',
                borderRadius: '2px', backgroundColor: 'rgba(147,142,164,0.12)',
                color: d.textSecondary, padding: '1px 6px', flexShrink: 0,
              }}>NO SPLIT</span>
            )}
          </div>
          <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300, color: d.textSecondary, marginTop: '2px', lineHeight: 1.25 }}>
            {payerName} paid{splitLabel ? ` · ${splitLabel}` : ''}
          </div>
        </div>
        {/* Right column: amount + /ea (tight pair) */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: displayFont, fontSize: fs.displaySmall, fontWeight: 700, color: d.accent, lineHeight: 1.1 }}>
            ${fmtDollars(expense.amount)}
          </div>
          {!isNoSplit && splitCount > 1 && (
            <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300, color: d.textSecondary, marginTop: '2px', lineHeight: 1.1 }}>
              ${fmtDollars(perEach)}/ea
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Ghost Expense Row (unlinked itinerary item) ─── */
function GhostExpenseRow({ item, onTap }: { item: ItineraryItem; onTap: () => void }) {
  const fs = useDesign().typography.fontSize;
  return (
    <div onClick={onTap} style={{
      border: '1px dashed rgba(200,146,58,0.22)', borderRadius: '2px',
      padding: '9px 14px', marginBottom: '6px', cursor: 'pointer',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.itemName, fontWeight: 400, color: 'rgba(147,142,164,0.55)' }}>
        {item.name}
      </span>
      <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 500, color: 'rgba(200,146,58,0.45)' }}>
        + add $
      </span>
    </div>
  );
}

/* ─── Log / Edit Expense Modal ─── */
function ExpenseModal({ tripId, members, tripDays, editExpense, prefillName, prefillDay, prefillAmount, onClose }: {
  tripId: string; members: Record<string, TripMember>; tripDays: string[];
  editExpense?: Expense; prefillName?: string; prefillDay?: string; prefillAmount?: string; onClose: () => void;
}) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const memberList = getDisplayMembers(members);
  const isEdit = !!editExpense;
  const [noSplit, setNoSplit] = useState(editExpense?.noSplit || false);
  const [description, setDescription] = useState(editExpense?.description || prefillName || '');
  const [day, setDay] = useState(editExpense?.day || prefillDay || tripDays[0] || '');
  const [amount, setAmount] = useState(editExpense ? String(Number(editExpense.amount)) : prefillAmount || '');
  const [paidByUid, setPaidByUid] = useState(editExpense?.paidByUid || memberList[0]?.uid || '');
  const [splitAmong, setSplitAmong] = useState<Record<string, boolean>>(() => {
    if (editExpense) {
      const map: Record<string, boolean> = {};
      memberList.forEach((m) => { map[m.uid] = (editExpense.splitAmong || []).includes(m.uid); });
      return map;
    }
    return Object.fromEntries(memberList.map((m) => [m.uid, true]));
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const toggleSplit = (uid: string) => {
    const updated = { ...splitAmong, [uid]: !splitAmong[uid] };
    if (Object.values(updated).filter(Boolean).length === 0 && !noSplit) updated[paidByUid] = true;
    setSplitAmong(updated);
  };

  const handleSubmit = async () => {
    const parsedAmount = parseFloat(amount);
    if (!description.trim() || isNaN(parsedAmount) || parsedAmount <= 0) return;
    setIsSubmitting(true); setSubmitError('');
    let splitUids = Object.keys(splitAmong).filter((uid) => splitAmong[uid]);
    if (!noSplit && splitUids.length === 0) splitUids = [paidByUid];
    try {
      if (isEdit) {
        await updateExpense(tripId, editExpense.expenseId, {
          description: description.trim(),
          ...(day ? { day } : { day: undefined }),
          amount: parsedAmount, paidByUid, splitAmong: splitUids, noSplit,
        });
      } else {
        await addExpense(tripId, {
          description: description.trim(),
          ...(day ? { day } : {}),
          amount: parsedAmount, paidByUid, splitAmong: splitUids, noSplit,
        });
      }
      onClose();
    } catch (err) {
      console.error('Failed to save expense:', err);
      setSubmitError('Failed to save — please try again');
    } finally { setIsSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!editExpense || !window.confirm(`Delete "${editExpense.description}"?`)) return;
    setIsSubmitting(true);
    try {
      await deleteExpense(tripId, editExpense.expenseId);
      onClose();
    } catch (err) {
      console.error('Failed to delete expense:', err);
      setSubmitError('Failed to delete');
    } finally { setIsSubmitting(false); }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 400,
    color: d.textPrimary, backgroundColor: d.bgSurface,
    border: '1px solid rgba(200,146,58,0.10)', borderRadius: '2px', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
    letterSpacing: '1.5px', textTransform: 'uppercase', color: d.textSecondary, marginBottom: '6px',
  };

  return (
    <Modal title={isEdit ? 'Edit Expense' : 'Log Expense'} onClose={onClose}>
      {/* No Split toggle */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: d.textSecondary }}>No Split</span>
        <button type="button" onClick={() => setNoSplit(!noSplit)} style={{
          width: '36px', height: '20px', borderRadius: '10px', border: 'none',
          backgroundColor: noSplit ? 'rgba(200,146,58,0.30)' : 'rgba(147,142,164,0.25)',
          position: 'relative', cursor: 'pointer',
        }}>
          <div style={{
            width: '14px', height: '14px', borderRadius: '50%',
            backgroundColor: noSplit ? d.accent : d.textSecondary,
            position: 'absolute', top: '3px', left: noSplit ? '19px' : '3px', transition: 'left 0.2s',
          }} />
        </button>
      </div>
      {/* What */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>What</label>
        <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
          onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
          style={inputStyle} />
      </div>
      {/* Day + Amount side by side */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Day</label>
          <select value={day} onChange={(e) => setDay(e.target.value)}
            onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
            style={inputStyle}>
            {tripDays.map((d) => <option key={d} value={d}>{new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</option>)}
            <option value="">Other</option>
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Amount</label>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="$"
            onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
            style={inputStyle} />
        </div>
      </div>
      {/* Paid By */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Paid By</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {memberList.map((m) => {
            const sel = paidByUid === m.uid;
            return <button key={m.uid} type="button" onClick={() => setPaidByUid(m.uid)} style={{
              padding: '6px 12px', borderRadius: '2px',
              border: `1px solid ${sel ? 'rgba(200,146,58,0.40)' : 'rgba(147,142,164,0.20)'}`,
              backgroundColor: 'transparent', color: sel ? d.accent : d.textSecondary,
              fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 400, cursor: 'pointer',
            }}>{m.displayName.split(' ')[0]}</button>;
          })}
        </div>
      </div>
      {/* Split Among */}
      {!noSplit && (
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Split Among</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {memberList.map((m) => {
              const sel = splitAmong[m.uid];
              return <button key={m.uid} type="button" onClick={() => toggleSplit(m.uid)} style={{
                padding: '6px 12px', borderRadius: '2px',
                border: `1px solid ${sel ? 'rgba(200,146,58,0.40)' : 'rgba(147,142,164,0.20)'}`,
                backgroundColor: 'transparent', color: sel ? d.accent : d.textSecondary,
                fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 400, cursor: 'pointer',
              }}>{m.displayName.split(' ')[0]}{sel ? ' ✓' : ''}</button>;
            })}
          </div>
        </div>
      )}
      {submitError && <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, color: d.negative, marginBottom: '8px', textAlign: 'center' }}>{submitError}</p>}
      <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
        {isEdit && (
          <button type="button" onClick={handleDelete} disabled={isSubmitting} style={{
            padding: '11px 16px', borderRadius: '2px',
            border: `1px solid ${d.negative}`, backgroundColor: 'transparent',
            fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
            letterSpacing: '3px', textTransform: 'uppercase',
            color: d.negative, cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.5 : 1,
          }}>Delete</button>
        )}
        <DesignButton tier={2} fullWidth onClick={handleSubmit} disabled={isSubmitting || !description.trim()}>
          {isSubmitting ? 'Saving...' : isEdit ? 'Save' : 'Add Expense'}
        </DesignButton>
      </div>
    </Modal>
  );
}

/* ─── Settle Up View ─── */
function SettleUpView({ expenses, members, tripId, settlements: existingSettlements, destination }: {
  expenses: Expense[]; members: Record<string, TripMember>; tripId: string; settlements?: Record<string, Settlement>; destination: string;
}) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const displayFont = `'${design.typography.displayFont}', serif`;
  const calculated = calculateSettlements(expenses, members);
  const memberList = getDisplayMembers(members);

  const balances: Record<string, { paid: number; share: number }> = {};
  const memberUids = new Set(
    getDisplayMembers(members).map((m) => m.uid)
  );
  memberUids.forEach((uid) => { balances[uid] = { paid: 0, share: 0 }; });
  expenses.forEach((expense) => {
    if (expense.noSplit) return;
    const sa = (expense.splitAmong || []).filter((uid) => memberUids.has(uid));
    if (sa.length === 0) return;
    const amt = Number(expense.amount);
    const share = amt / sa.length;
    if (balances[expense.paidByUid]) balances[expense.paidByUid].paid += amt;
    sa.forEach((uid) => { if (balances[uid]) balances[uid].share += share; });
  });

  const toggleSettled = async (index: number) => {
    const settlement = calculated[index];
    const settlementId = `${settlement.fromUid}_${settlement.toUid}`;
    const current = existingSettlements?.[settlementId];
    const newSettled = !current?.settled;
    await saveSettlement(tripId, settlementId, {
      ...settlement, settled: newSettled,
      ...(newSettled ? { settledAt: Date.now() } : {}),
    });
  };

  return (
    <div>
      {calculated.map((settlement, i) => {
        const settlementId = `${settlement.fromUid}_${settlement.toUid}`;
        const isSettled = existingSettlements?.[settlementId]?.settled || false;
        const fromName = members[settlement.fromUid]?.displayName?.split(' ')[0] || 'Unknown';
        const toName = members[settlement.toUid]?.displayName?.split(' ')[0] || 'Unknown';
        return (
          <div key={i} onClick={() => toggleSettled(i)} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
            cursor: 'pointer', opacity: isSettled ? 0.55 : 1,
          }}>
            <span style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.body, fontWeight: 500,
              color: isSettled ? 'rgba(237,228,216,0.50)' : d.textPrimary,
              textDecoration: isSettled ? 'line-through' : 'none',
            }}>{fromName} → {toName}</span>
            {isSettled ? (
              <span style={{
                fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 500,
                letterSpacing: '1.5px', textTransform: 'uppercase', color: d.confirmed,
              }}>Settled ✓</span>
            ) : (
              <span style={{ fontFamily: displayFont, fontSize: fs.displaySmall, fontWeight: 700, color: d.accent }}>
                ${fmtDollars(settlement.amount)}
              </span>
            )}
          </div>
        );
      })}

      {calculated.length === 0 && (
        <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 400, fontStyle: 'italic', color: 'rgba(147,142,164,0.60)', textAlign: 'center', padding: '24px' }}>
          No settlements needed
        </p>
      )}

      {/* Breakdown */}
      <div style={{ marginTop: '28px' }}>
        <div style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
          letterSpacing: '3px', textTransform: 'uppercase', color: d.textSecondary,
          paddingBottom: '8px', borderBottom: '1px solid rgba(200,146,58,0.12)', marginBottom: '6px',
        }}>Breakdown</div>

        {memberList.map((member) => {
          const b = balances[member.uid] || { paid: 0, share: 0 };
          const net = b.paid - b.share;
          const netColor = net > 0 ? d.confirmed : net < 0 ? d.negative : d.textTertiary;
          return (
            <div key={member.uid} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)',
            }}>
              <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.body, fontWeight: 500, color: d.textPrimary, width: '76px', flexShrink: 0 }}>
                {member.displayName.split(' ')[0]}
              </span>
              <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300, color: d.textSecondary, flex: 1, textAlign: 'right', paddingRight: '10px' }}>
                Paid ${fmtDollars(b.paid)} · Share ${fmtDollars(b.share)}
              </span>
              <span style={{ fontFamily: displayFont, fontSize: fs.bodyLarge, fontWeight: 700, color: netColor, minWidth: '55px', textAlign: 'right' }}>
                {net >= 0 ? '+' : '-'}${fmtDollars(Math.abs(net))}
              </span>
            </div>
          );
        })}
      </div>

      {/* Design closer */}
      <div style={{ padding: '28px 0' }}>
        <OrnamentalDivider style={{ margin: '0 0 12px' }} />
        <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.wordmark, fontWeight: 800, letterSpacing: '9px', textTransform: 'uppercase', color: 'rgba(200,146,58,0.20)', textAlign: 'center', marginBottom: '4px' }}>KREWE</div>
        <div style={{ fontFamily: displayFont, fontSize: fs.detail, fontWeight: 400, fontStyle: 'italic', color: 'rgba(200,146,58,0.25)', textAlign: 'center' }}>{destination}</div>
      </div>
    </div>
  );
}

/* ─── Main ExpensesTab ─── */
export default function ExpensesTab({ trip, tripId, canEdit, currentUid }: ExpensesTabProps) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const displayFont = `'${design.typography.displayFont}', serif`;
  const [subTab, setSubTab] = useState<SubTab>('expenses');
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | undefined>(undefined);
  const [prefillName, setPrefillName] = useState('');
  const [prefillDay, setPrefillDay] = useState('');
  const [prefillAmount, setPrefillAmount] = useState('');

  const members = trip.members || {};
  const meta = trip.meta;
  const tripDays = getTripDays(meta.startDate, meta.endDate);

  const expenses: Expense[] = trip.expenses ? Object.values(trip.expenses) : [];
  const settlementData = trip.settlements;

  // Coerce amounts to numbers — Firebase may store them as strings
  const totalSpent = expenses.reduce((sum, e) => sum + Number(e.amount), 0);
  const splitExpenses = expenses.filter((e) => !e.noSplit);
  const noSplitExpenses = expenses.filter((e) => e.noSplit);
  const splittableTotal = splitExpenses.reduce((sum, e) => sum + Number(e.amount), 0);
  // Per-person only counts non-anonymous members
  const realMemberCount = getDisplayMembers(members).length;
  const perPerson = realMemberCount > 0 ? splittableTotal / realMemberCount : 0;

  // Dev debug summary
  if (process.env.NODE_ENV === 'development' && expenses.length > 0) {
    console.log(
      `[Expenses] Total: $${totalSpent.toFixed(2)} from ${expenses.length} expenses ` +
      `(${splitExpenses.length} split, ${noSplitExpenses.length} no-split). ` +
      `Splittable: $${splittableTotal.toFixed(2)}, Per person: $${perPerson.toFixed(2)} (${realMemberCount} members)`
    );
    expenses.forEach((e) => {
      console.log(`  - "${e.description}" $${Number(e.amount)} (type: ${typeof e.amount}) ${e.noSplit ? '[NO SPLIT]' : `[split ${(e.splitAmong || []).length} ways]`}`);
    });
  }

  // Group expenses by day — only into trip days, everything else goes to "Other"
  const tripDaySet = new Set(tripDays);
  const grouped: Record<string, Expense[]> = {};
  const otherExpenses: Expense[] = [];
  expenses.forEach((e) => {
    if (e.day && tripDaySet.has(e.day)) {
      if (!grouped[e.day]) grouped[e.day] = [];
      grouped[e.day].push(e);
    } else {
      otherExpenses.push(e);
    }
  });

  // Ghost rows: itinerary items without linked expenses
  const itineraryItems: ItineraryItem[] = trip.itinerary ? Object.values(trip.itinerary) : [];
  const linkedItemIds = new Set(expenses.filter((e) => e.linkedItemId).map((e) => e.linkedItemId));
  const unlinkedByDay: Record<string, ItineraryItem[]> = {};
  const undatedGhosts: ItineraryItem[] = [];
  itineraryItems.forEach((item) => {
    if (linkedItemIds.has(item.itemId)) return;
    if (item.day) {
      if (!unlinkedByDay[item.day]) unlinkedByDay[item.day] = [];
      unlinkedByDay[item.day].push(item);
    } else {
      undatedGhosts.push(item);
    }
  });

  // KREWE fee ghost row: show if trip is paid and no KREWE expense exists yet
  const hasKreweExpense = expenses.some((e) => e.description.toLowerCase() === 'krewe app');
  const showKreweGhost = meta.isPaid && !hasKreweExpense && canEdit;

  const openExpenseModal = (name?: string, day?: string, amount?: string) => {
    setEditingExpense(undefined);
    setPrefillName(name || '');
    setPrefillDay(day || '');
    setPrefillAmount(amount || '');
    setShowExpenseModal(true);
  };

  const openEditModal = (expense: Expense) => {
    setEditingExpense(expense);
    setPrefillName('');
    setPrefillDay('');
    setPrefillAmount('');
    setShowExpenseModal(true);
  };

  return (
    <div style={{ padding: '0 26px' }}>
      {/* ═══ SUMMARY CARD ═══ */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        border: '1px solid rgba(200,146,58,0.12)', borderRadius: '2px',
        padding: '14px 16px', marginBottom: '16px', backgroundColor: d.bgSurface,
      }}>
        <div>
          <span style={{ display: 'block', fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: d.textSecondary }}>Total Spent</span>
          <span style={{ fontFamily: displayFont, fontSize: '21px', fontWeight: 700, color: d.accent }}>${fmtDollars(totalSpent)}</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={{ display: 'block', fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: d.textSecondary }}>Per Person</span>
          <span style={{ fontFamily: displayFont, fontSize: '21px', fontWeight: 700, color: d.textPrimary }}>~${fmtDollars(perPerson)}</span>
        </div>
      </div>

      {/* ═══ SUB-TABS ═══ */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
        {(['expenses', 'settle'] as SubTab[]).map((tab) => {
          const isActive = subTab === tab;
          return (
            <button key={tab} type="button" onClick={() => setSubTab(tab)} style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 500,
              padding: '10px 20px', minHeight: '44px',
              border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
              color: isActive ? d.textPrimary : d.textSecondary,
              borderBottom: isActive ? `1.5px solid ${d.accent}` : '1.5px solid transparent',
            }}>
              {tab === 'expenses' ? 'Expenses' : 'Settle Up'}
            </button>
          );
        })}
      </div>

      {subTab === 'expenses' ? (
        <>
          {/* Log Expense CTA — visible to planner and claimed crew only */}
          {canEdit && (
            <div style={{ marginBottom: '20px' }}>
              <DesignButton tier={2} fullWidth onClick={() => openExpenseModal()}>
                Log Expense
              </DesignButton>
            </div>
          )}

          {/* KREWE fee ghost row */}
          {showKreweGhost && (
            <div onClick={() => openExpenseModal('KREWE app', '', '30')} style={{
              border: '1px dashed rgba(200,146,58,0.22)', borderRadius: '2px',
              padding: '9px 14px', marginBottom: '6px', cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.itemName, fontWeight: 400, color: 'rgba(147,142,164,0.55)' }}>
                KREWE app
              </span>
              <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 500, color: 'rgba(200,146,58,0.45)' }}>
                + add $30
              </span>
            </div>
          )}

          {/* Day groups */}
          {tripDays.map((day) => {
            const dayExpenses = grouped[day] || [];
            const dayGhosts = unlinkedByDay[day] || [];
            if (dayExpenses.length === 0 && dayGhosts.length === 0) return null;
            const { dayName, dateLabel } = formatDayHeader(day);

            // Interleave: logged expenses first, then ghosts (sorted by time)
            const ghostsSorted = dayGhosts.sort((a, b) => (a.time || '').localeCompare(b.time || ''));

            return (
              <div key={day} style={{ marginBottom: '20px' }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                  paddingBottom: '8px', borderBottom: '1px solid rgba(200,146,58,0.12)', marginBottom: '8px',
                }}>
                  <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.dayHeader, fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: d.textSecondary }}>{dayName}</span>
                  <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.dayHeader, fontWeight: 300, color: d.textTertiary }}>{dateLabel}</span>
                </div>
                {dayExpenses.map((expense) => (
                  <ExpenseRow key={expense.expenseId} expense={expense} members={members} onEdit={() => openEditModal(expense)} />
                ))}
                {canEdit && ghostsSorted.map((item) => (
                  <GhostExpenseRow key={item.itemId} item={item} onTap={() => openExpenseModal(item.name, day)} />
                ))}
              </div>
            );
          })}

          {/* Other (undated expenses + undated ghost rows) */}
          {(otherExpenses.length > 0 || (canEdit && undatedGhosts.length > 0)) && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ paddingBottom: '8px', borderBottom: '1px solid rgba(200,146,58,0.12)', marginBottom: '8px' }}>
                <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.dayHeader, fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: d.textTertiary }}>Other</span>
              </div>
              {otherExpenses.map((expense) => (
                <ExpenseRow key={expense.expenseId} expense={expense} members={members} onEdit={() => openEditModal(expense)} />
              ))}
              {canEdit && undatedGhosts.map((item) => (
                <GhostExpenseRow key={item.itemId} item={item} onTap={() => openExpenseModal(item.name)} />
              ))}
            </div>
          )}

          {expenses.length === 0 && itineraryItems.length === 0 && (
            <div style={{ padding: '48px 16px 24px', textAlign: 'center' }}>
              <p style={{
                fontFamily: displayFont, fontSize: fs.bodyLarge, fontWeight: 600,
                fontStyle: 'italic', color: d.textSecondary,
                margin: 0, lineHeight: 1.4,
              }}>
                No expenses logged yet.<br />Split the first bill and the app handles the rest.
              </p>
            </div>
          )}

          {/* Design closer */}
          <div style={{ padding: '28px 0' }}>
            <OrnamentalDivider style={{ margin: '0 0 12px' }} />
            <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.wordmark, fontWeight: 800, letterSpacing: '9px', textTransform: 'uppercase', color: 'rgba(200,146,58,0.20)', textAlign: 'center', marginBottom: '4px' }}>KREWE</div>
            <div style={{ fontFamily: displayFont, fontSize: fs.detail, fontWeight: 400, fontStyle: 'italic', color: 'rgba(200,146,58,0.25)', textAlign: 'center' }}>{meta.destination}</div>
          </div>
        </>
      ) : (
        <SettleUpView expenses={expenses} members={members} tripId={tripId} settlements={settlementData} destination={meta.destination} />
      )}

      {showExpenseModal && (
        <ExpenseModal tripId={tripId} members={members} tripDays={tripDays}
          editExpense={editingExpense} prefillName={prefillName} prefillDay={prefillDay} prefillAmount={prefillAmount}
          onClose={() => { setShowExpenseModal(false); setEditingExpense(undefined); }}
        />
      )}
    </div>
  );
}
